import React, { useState, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { importProducts, clearAdminPassword } from '../utils/api';

export default function ImportPage() {
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const fileInputRef = useRef(null);
  const navigate = useNavigate();

  function handleFileChange(e) {
    const selected = e.target.files[0];
    setFile(selected || null);
    setResult(null);
    setError(null);
  }

  async function handleUpload() {
    if (!file) return;
    setLoading(true);
    setResult(null);
    setError(null);
    try {
      const data = await importProducts(file);
      setResult(data);
      setFile(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
    } catch (err) {
      if (err.status === 401) {
        clearAdminPassword();
        navigate('/');
        return;
      }
      setError(err);
    } finally {
      setLoading(false);
    }
  }

  return (
    <Layout title="Import">
      <style>{css}</style>

      <div className="page-head">
        <h1 className="h1">Import Products</h1>
      </div>

      <div className="card">
        <p className="helper">
          Upload a CSV with columns: brand, manufacturer, model, category, flex, hand, blade_curve, price, stock_quantity, sku, image_url
        </p>

        <div className="dropzone">
          <input
            ref={fileInputRef}
            type="file"
            accept=".csv"
            onChange={handleFileChange}
            className="file-input"
          />
          {file && <p className="file-name">Selected: {file.name}</p>}
        </div>

        <button
          onClick={handleUpload}
          disabled={!file || loading}
          className="btn-primary"
          style={{ width: '100%', opacity: !file || loading ? 0.5 : 1, cursor: !file || loading ? 'not-allowed' : 'pointer' }}
        >
          {loading ? 'Uploading...' : 'Upload CSV'}
        </button>
      </div>

      {result && (
        <div className="card">
          <h2 className="card-title">Import successful</h2>
          <div className="summary">
            <SummaryRow label="Rows processed" value={result.summary.rows_processed} />
            <SummaryRow label="Products created" value={result.summary.products_created} />
            <SummaryRow label="Products matched" value={result.summary.products_found} />
            <SummaryRow label="Variants created" value={result.summary.variants_created} />
            <SummaryRow label="Variants updated" value={result.summary.variants_updated} />
          </div>
          <Link to="/products" className="btn-primary" style={{ display: 'inline-block', marginTop: 16, textDecoration: 'none' }}>View products →</Link>
        </div>
      )}

      {error && (
        <div className="card" style={{ borderLeft: '4px solid #d70015' }}>
          <h2 className="card-title">Import failed</h2>
          <p className="error-text">{error.message}</p>
          {error.detail && <p className="error-detail">{error.detail}</p>}
          {error.errors && error.errors.length > 0 && (
            <ul className="error-list">
              {error.errors.map((msg, i) => <li key={i} className="error-item">{msg}</li>)}
            </ul>
          )}
        </div>
      )}
    </Layout>
  );
}

function SummaryRow({ label, value }) {
  return (
    <div className="summary-row">
      <span className="summary-label">{label}</span>
      <span className="summary-value">{value}</span>
    </div>
  );
}

const css = `
  .page-head { margin-bottom: 20px; }
  .h1 { margin: 0; font-size: 28px; font-weight: 600; color: #1d1d1f; }
  .card {
    background: #fff;
    border-radius: 20px;
    box-shadow: 0 2px 20px rgba(0,0,0,0.06);
    padding: 32px;
    margin-bottom: 20px;
  }
  .card-title { margin: 0 0 12px 0; font-size: 18px; font-weight: 600; color: #1d1d1f; }
  .helper { color: #86868b; font-size: 14px; margin: 0 0 20px 0; line-height: 1.5; }
  .dropzone {
    border: 2px dashed #d2d2d7;
    border-radius: 12px;
    padding: 24px;
    text-align: center;
    margin-bottom: 20px;
  }
  .file-input { display: block; margin: 0 auto; }
  .file-name { margin-top: 12px; font-size: 14px; color: #1d1d1f; }
  .btn-primary {
    padding: 12px 16px;
    border-radius: 12px;
    border: none;
    background: #0071e3;
    color: #fff;
    font-size: 16px;
    font-weight: 500;
    font-family: inherit;
  }
  .summary { display: flex; flex-direction: column; gap: 8px; }
  .summary-row {
    display: flex; justify-content: space-between;
    padding: 8px 0; border-bottom: 1px solid #f0f0f2;
  }
  .summary-label { color: #86868b; font-size: 14px; }
  .summary-value { color: #1d1d1f; font-size: 14px; font-weight: 500; }
  .error-text { color: #1d1d1f; margin: 0; font-size: 15px; }
  .error-detail { color: #86868b; font-size: 13px; margin: 8px 0 0 0; font-family: ui-monospace, monospace; }
  .error-list { margin: 12px 0 0 0; padding-left: 20px; }
  .error-item { color: #1d1d1f; font-size: 13px; margin-bottom: 4px; font-family: ui-monospace, monospace; }

  @media (max-width: 700px) {
    .h1 { font-size: 22px; }
    .card { padding: 20px 16px; border-radius: 16px; }
  }
`;
